/**
 * Helpers de APRESENTAÇÃO (derivados que o front calcula — não vêm da API):
 * chip de SLA (classe + rótulo), tempo relativo e ações da linha do tempo de
 * eventos (eventAction).
 */
import {slaChip} from './ticketsHelpers';
import {STATUS_META, PRIORITY_META} from './ticketsMeta';
import {
  Ticket,
  TicketEvent,
  TicketStatus,
  TicketPriority,
} from './models/Ticket';

/**
 * O texto da bolinha do responsável onde ela é a única indicação de quem é (o
 * card do Quadro e a linha da Lista): o nome acessível e a dica no hover.
 */
export const assigneeTitle = (name: string | null): string =>
  name?.trim() ? `Responsável: ${name.trim()}` : 'Sem responsável';

export interface SlaView {
  cls: string; // classe de cor por criticidade
  late: boolean; // já venceu?
  remaining: string; // tempo legível: "11d 2h", "3h", "<1h"
  deadline: string; // data limite: "15/06 14:00"
}

// Horas absolutas → texto curto e legível. 266h → "11d 2h", 66h → "2d 18h".
const humanizeHours = (hoursAbs: number): string => {
  if (hoursAbs < 1) return '<1h';
  if (hoursAbs < 24) return `${hoursAbs}h`;
  const d = Math.floor(hoursAbs / 24);
  const h = hoursAbs % 24;
  return h ? `${d}d ${h}h` : `${d}d`;
};

// ISO → data limite curta no fuso local: "15/06 14:00".
const formatDeadline = (iso: string): string => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(
    d.getMinutes()
  )}`;
};

// Enriquece o chip puro (slaChip) com tempo legível, data limite e cor por
// criticidade: vermelho (atrasado OU ≤6h), âmbar (≤24h), verde (>24h).
export const slaChipView = (
  dueAt: string | null,
  now?: Date
): SlaView | null => {
  const c = slaChip(dueAt, now);
  if (!c || !dueAt) return null;
  const cls =
    c.late || c.hours <= 6
      ? 'opx-chip--sla-late'
      : c.hours <= 24
      ? 'opx-chip--sla-warn'
      : 'opx-chip--sla-ok';
  return {
    cls,
    late: c.late,
    remaining: humanizeHours(c.hours),
    deadline: formatDeadline(dueAt),
  };
};

export const fromNow = (iso: string, now: number = Date.now()): string => {
  const diffMs = now - new Date(iso).getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.round(h / 24);
  return d === 1 ? 'ontem' : `há ${d} dias`;
};

/**
 * Relógio de RESPOSTA (item 6). Distinto do SLA de resolução acima: mede SILÊNCIO
 * desde a última resposta, não o prazo contado da criação. Um ticket pode estar
 * dentro do prazo de resolução e mesmo assim há dias sem ninguém responder.
 *
 * Devolve null quando `response_due_at` é null — o backend pausa o relógio em
 * aguardando_cliente/resolvido/fechado, e nesses casos não há nada a cobrar.
 *
 * Um ticket nunca respondido conta desde a criação (o backend faz o mesmo no
 * `response_due_at`): aberto e nunca respondido é o pior caso, não o isento.
 */
export interface ResponseClockView {
  late: boolean;
  neverAnswered: boolean;
  silence: string; // "há 12 min"
  deadline: string; // "26/08 14:17"
  label: string;
  title: string;
}

export const responseClockView = (
  t: Pick<Ticket, 'response_due_at' | 'last_response_at' | 'created_at'>,
  now: Date = new Date()
): ResponseClockView | null => {
  if (!t.response_due_at) return null;
  const neverAnswered = !t.last_response_at;
  const silence = fromNow(t.last_response_at ?? t.created_at, now.getTime());
  const deadline = formatDeadline(t.response_due_at);
  const late = new Date(t.response_due_at).getTime() < now.getTime();
  return {
    late,
    neverAnswered,
    silence,
    deadline,
    label: neverAnswered ? `Sem resposta ${silence}` : `Resposta ${silence}`,
    title: late
      ? `Prazo de resposta venceu ${deadline}`
      : `Responder até ${deadline}`,
  };
};

// ---------- Atividade (timeline de eventos) ----------
const statusLabel = (k: unknown): string =>
  typeof k === 'string' && k in STATUS_META
    ? STATUS_META[k as TicketStatus].label
    : String(k ?? '');

const priorityLabel = (k: unknown): string =>
  typeof k === 'string' && k in PRIORITY_META
    ? PRIORITY_META[k as TicketPriority].label
    : String(k ?? '');

// Eventos com texto livre (comentário/nota/sistema) mostram o corpo abaixo do autor;
// os demais são "estruturados" e viram uma frase de ação (com de→para de metadata).
export const STRUCTURED = new Set<TicketEvent['type']>([
  'created',
  'assigned',
  'status_changed',
  'priority_changed',
]);

// Frase da ação de um evento estruturado, usando metadata.from/to do servidor.
export const eventAction = (e: TicketEvent): string => {
  const from = e.metadata?.from;
  const to = e.metadata?.to;
  switch (e.type) {
    case 'created':
      return 'criou o ticket';
    case 'assigned': {
      const fromName =
        typeof e.metadata?.from_name === 'string' ? e.metadata.from_name : null;
      const toName =
        typeof e.metadata?.to_name === 'string' ? e.metadata.to_name : null;
      if (!to)
        return fromName
          ? `removeu ${fromName} dos responsáveis`
          : 'removeu o responsável';
      if (to === e.actor_uid) return 'assumiu o ticket';
      if (fromName && toName)
        return `alterou o responsável de ${fromName} para ${toName}`;
      if (toName) return `atribuiu para ${toName}`;
      return 'alterou o responsável';
    }
    case 'status_changed':
      return from
        ? `moveu de ${statusLabel(from)} para ${statusLabel(to)}`
        : `mudou o status para ${statusLabel(to)}`;
    case 'priority_changed':
      return from
        ? `mudou a prioridade de ${priorityLabel(from)} para ${priorityLabel(
            to
          )}`
        : `mudou a prioridade para ${priorityLabel(to)}`;
    default:
      return 'registrou um evento';
  }
};
